import EditIcon from '@mui/icons-material/Edit';
import PriceAnalysisIcon from '@mui/icons-material/PriceCheck';
import RecommendIcon from '@mui/icons-material/Recommend';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import type { GridColDef, GridFilterItem, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import CustomDataGrid from 'components/common/CustomDataGrid';
import MasterFilter, {
  type FilterFieldConfig,
  type FilterOption,
  useMasterFilter,
} from 'components/common/MasterFilter';
import SmartFilterModal from 'components/common/MasterFilter/SmartFilterModal';
import { gridItemsToActiveFilters } from 'components/common/MasterFilter/smartFilterMapping';
import PillTabs from 'components/common/PillTabs';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import useCurrentUser from 'hooks/useCurrentUser';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetCarsQuery } from 'services/api/cars.api';
import priceFormat from 'services/format/priceFormat';
import type { Car } from 'services/types/car';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import carsFilterFields from './carsFilterFields';
import PriceAnalysisModal from './PriceAnalysisModal';
import RecommendClientsModal from './RecommendClientsModal';
import SoldCarsAnalysis from './SoldCarsAnalysis';

const parseQueryParam = <T,>(param: string | null, defaultValue: T): T => {
  if (!param) return defaultValue;
  try {
    return JSON.parse(param) as T;
  } catch {
    return defaultValue;
  }
};

const Cars = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useCurrentUser();

  // Parse query params for initial state
  const queryPaginationModel = searchParams.get('paginationModel');
  const queryFilterModel = searchParams.get('filterModel');
  const querySortModel = searchParams.get('sortModel');

  const parsedPaginationModel = parseQueryParam<GridPaginationModel>(queryPaginationModel, { page: 0, pageSize: 50 });

  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const businessUnitId = selectedBusinessUnitId ?? -1;
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    parsedPaginationModel.pageSize <= 100 && parsedPaginationModel.page >= 0
      ? parsedPaginationModel
      : { page: 0, pageSize: 50 }
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    parseQueryParam<GridFilterModel>(queryFilterModel, { items: [] })
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(parseQueryParam<GridSortModel>(querySortModel, []));
  const [openSoldCarsAnalysis, setOpenSoldCarsAnalysis] = useState(false);
  const [isAdvancedOpen, setAdvancedOpen] = useState(false);

  // ── MasterFilter: bar config ──────────────────────────────────────────────
  const barConfig = useMemo(
    (): FilterFieldConfig[] => [
      {
        key: 'searchTerm',
        kind: 'text',
        label: 'Buscar',
        placement: 'bar',
        placeholder: 'Marca, modelo, versión o patente',
      },
      { key: 'brands', kind: 'multiSelect', label: 'Marca', placement: 'bar' },
      { key: 'models', kind: 'multiSelect', label: 'Modelo', placement: 'bar' },
    ],
    []
  );

  // URL persistence for all bar filters (search, brands, models) under the `cars.` namespace.
  const masterFilter = useMasterFilter(barConfig, businessUnitId, undefined, undefined, {
    urlNamespace: 'cars',
  });

  // Debounce the BUSCAR input so we don't re-query on every keystroke.
  const rawSearch = (masterFilter.values.searchTerm as string | undefined) ?? '';
  const [debouncedSearch, setDebouncedSearch] = useState<string>(rawSearch);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(rawSearch), 300);
    return () => clearTimeout(t);
  }, [rawSearch]);

  // Derive bar → GridFilterItems (brands/models → in).
  const barFilterItems = useMemo((): GridFilterItem[] => {
    const items: GridFilterItem[] = [];
    const brands = masterFilter.values.brands as string[] | undefined;
    if (brands?.length) items.push({ field: 'brand', operator: 'in', value: brands.join(',') });
    const models = masterFilter.values.models as string[] | undefined;
    if (models?.length) items.push({ field: 'model', operator: 'in', value: models.join(',') });
    return items;
  }, [masterFilter.values]);

  // ── URL ↔ state sync (bar filters handled by useMasterFilter's persistence) ──
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const isDefault = paginationModel.page === 0 && paginationModel.pageSize === 50;
        if (isDefault) {
          prev.delete('paginationModel');
          return prev;
        }
        prev.set('paginationModel', JSON.stringify(paginationModel));
        return prev;
      },
      { replace: true }
    );
  }, [paginationModel, setSearchParams]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (filterModel.items.length > 0) {
          prev.set('filterModel', JSON.stringify(filterModel));
        } else {
          prev.delete('filterModel');
        }
        return prev;
      },
      { replace: true }
    );
  }, [filterModel, setSearchParams]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (sortModel.length > 0) {
          prev.set('sortModel', JSON.stringify(sortModel));
        } else {
          prev.delete('sortModel');
        }
        return prev;
      },
      { replace: true }
    );
  }, [sortModel, setSearchParams]);

  // Reset to page 0 whenever the row set changes (search, bar filters, advanced filters / isUsed tab).
  // Sort is intentionally excluded — it only reorders the same set.
  const prevFiltersKey = useRef<string | null>(null);
  useEffect(() => {
    const key = JSON.stringify({ debouncedSearch, barFilterItems, items: filterModel.items });
    if (prevFiltersKey.current === null) {
      prevFiltersKey.current = key;
      return;
    }
    if (prevFiltersKey.current === key) return;
    prevFiltersKey.current = key;
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [debouncedSearch, barFilterItems, filterModel.items]);

  // ── Server query: merge bar-derived items with advanced filterModel ────────
  const combinedFilterModel: GridFilterModel = useMemo(
    () => ({ items: [...barFilterItems, ...filterModel.items] }),
    [barFilterItems, filterModel.items]
  );

  const { data: carsData } = useGetCarsQuery({
    businessUnitId,
    paginationModel: JSON.stringify(paginationModel),
    filterModel: JSON.stringify(combinedFilterModel),
    sortModel: JSON.stringify(sortModel),
    searchTerm: debouncedSearch,
  });

  // Dynamic options for MARCA / MODELO — come from the backend aggregations (full BU inventory,
  // independent of the current filters) so the dropdowns stay populated as the user narrows.
  // Models are scoped to the currently selected brands (if any).
  const selectedBrands = useMemo(
    () => (masterFilter.values.brands as string[] | undefined) ?? [],
    [masterFilter.values.brands]
  );
  const dynamicOptions: Record<string, FilterOption[]> = useMemo(() => {
    const brandOpts = (carsData?.aggregations?.brands ?? []).map(({ value, count }) => ({
      value,
      label: `${value} (${count})`,
    }));
    const allModels = carsData?.aggregations?.models ?? [];
    const visibleModels = selectedBrands.length ? allModels.filter((m) => selectedBrands.includes(m.brand)) : allModels;
    const modelOpts = visibleModels.map(({ value, count }) => ({ value, label: `${value} (${count})` }));
    return { brands: brandOpts, models: modelOpts };
  }, [carsData?.aggregations, selectedBrands]);

  // When the user narrows brands, drop any previously selected models that no longer belong to a
  // selected brand — avoids the dead-end state where filters yield zero rows for non-obvious reasons.
  const { setValue: setMasterValue } = masterFilter;
  useEffect(() => {
    if (!selectedBrands.length) return;
    const allModels = carsData?.aggregations?.models ?? [];
    const validModelValues = new Set(allModels.filter((m) => selectedBrands.includes(m.brand)).map((m) => m.value));
    const currentModels = (masterFilter.values.models as string[] | undefined) ?? [];
    const pruned = currentModels.filter((m) => validModelValues.has(m));
    if (pruned.length !== currentModels.length) {
      setMasterValue('models', pruned);
    }
  }, [selectedBrands, carsData?.aggregations?.models, masterFilter.values.models, setMasterValue]);

  // Exclude `isUsed` from advanced chips — TODOS/USADOS/NUEVOS tabs already surface that choice.
  const advancedChips = useMemo(
    () =>
      gridItemsToActiveFilters(
        filterModel.items.filter((i) => i.field !== 'isUsed'),
        carsFilterFields
      ),
    [filterModel.items]
  );

  // ── Modal / row interactions ──────────────────────────────────────────────
  const selectedCarId = searchParams.get('id') ? parseInt(searchParams.get('id') || '', 10) : undefined;
  const isAnalyzePriceOpen = searchParams.get('analyzePrice') === 'true';
  const isRecommendClientsOpen = searchParams.get('recommendClients') === 'true';

  const handleOpenRecommendModal = (car: Car) => {
    setSearchParams((prev) => {
      prev.set('id', car.id.toString());
      prev.set('recommendClients', 'true');
      return prev;
    });
  };

  const handleOpenPriceAnalysisModal = (car: Car) => {
    setSearchParams((prev) => {
      prev.set('id', car.id.toString());
      prev.set('analyzePrice', 'true');
      return prev;
    });
  };

  const handleCloseRecommendModal = () => {
    setSearchParams((prev) => {
      prev.delete('recommendClients');
      return prev;
    });
  };

  const handleClosePriceAnalysisModal = () => {
    setSearchParams((prev) => {
      prev.delete('analyzePrice');
      return prev;
    });
  };

  const columns: GridColDef[] = [
    { field: 'id', width: 70, headerName: 'ID Relif' },
    {
      field: 'businessUnit',
      headerName: 'Unidad de Negocio',
      width: 200,
      valueFormatter: (value: { name: string }) => value.name,
    },
    { field: 'referenceId', headerName: 'ID Empresa' },
    { field: 'brand', width: 130, headerName: 'Marca' },
    { field: 'model', width: 130, headerName: 'Modelo' },
    { field: 'version', width: 250, headerName: 'Versión' },
    { field: 'year', width: 70, headerName: 'Año' },
    { field: 'km', width: 80, headerName: 'Kms' },
    {
      field: 'price',
      width: 120,
      valueFormatter: (value) => priceFormat.format(value as number),
      headerName: 'Precio',
    },
    { field: 'promotions', width: 300, headerName: 'Promociones' },
    { field: 'transmission', width: 120, headerName: 'Transmisión' },
    { field: 'carType', width: 120, headerName: 'Tipo' },
    { field: 'fuelType', width: 130, headerName: 'Combustible' },
    { field: 'isUsed', width: 70, valueFormatter: (value) => (value ? 'Usado' : 'Nuevo'), headerName: 'Tipo Venta' },
    { field: 'branchOfficeDirection', width: 250, headerName: 'Sucursal' },
    { field: 'color', width: 150, headerName: 'Color' },
    { field: 'createdAt', width: 200, headerName: 'Creación' },
    { field: 'updatedAt', width: 200, headerName: 'Actualización' },
    { field: 'licensePlate', width: 100, headerName: 'Patente' },
    {
      field: 'link',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <Link href={params.value as string} target="_blank" rel="noreferrer" underline="hover">
            Ver auto
          </Link>
        ) : (
          '-'
        ),
    },
    {
      field: 'image',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <Link href={params.value as string} target="_blank" rel="noreferrer" underline="hover">
            Ver foto
          </Link>
        ) : (
          '-'
        ),
    },
    {
      field: 'technicalSheet',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <Link href={params.value as string} target="_blank" rel="noreferrer" underline="hover">
            Ficha técnica
          </Link>
        ) : (
          '-'
        ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: user?.isAdmin ? 140 : 50,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Editar auto">
            <IconButton
              size="small"
              href={`/cars/${(params.row as Car).id}/edit`}
              target="_blank"
              rel="noopener"
              onClick={(e) => e.stopPropagation()}
              sx={{ color: 'text.secondary' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {user?.isAdmin && (
            <>
              <Tooltip title="Recomendar a clientes">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenRecommendModal(params.row as Car);
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <RecommendIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Analizar precio">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenPriceAnalysisModal(params.row as Car);
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <PriceAnalysisIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  if (user?.isAdmin) {
    columns.push({
      field: 'secondaryPrice',
      width: 120,
      valueFormatter: (value) => priceFormat.format(value as number),
      headerName: 'Precio Secundario',
    });
    columns.push({
      field: 'monthlyFee',
      width: 100,
      valueFormatter: (value) => priceFormat.format(value as number),
      headerName: 'Cuota Mensual',
    });
    columns.push({ field: 'category', width: 120, headerName: 'Categoría' });
    columns.push({ field: 'equipment', width: 500, renderCell: (params) => JSON.stringify(params.value) });
  }

  const isUsedFilterValue =
    (filterModel.items.find((item) => item.field === 'isUsed')?.value as boolean | undefined) ?? null;
  const isUsedToggleValue: 'all' | 'used' | 'new' =
    isUsedFilterValue === null ? 'all' : isUsedFilterValue ? 'used' : 'new';

  const handleIsUsedToggle = (newValue: 'all' | 'used' | 'new') => {
    const newFilterModel = { items: filterModel.items.filter((item) => item.field !== 'isUsed') };
    if (newValue !== 'all') {
      newFilterModel.items.push({
        field: 'isUsed',
        operator: 'equals',
        value: newValue === 'used',
      });
    }
    setFilterModel(newFilterModel);
  };

  const isUsedTabs = [
    { value: 'all' as const, label: 'Todos' },
    { value: 'used' as const, label: 'Usados' },
    { value: 'new' as const, label: 'Nuevos' },
  ];

  const pillBtnSx = {
    height: 32,
    borderRadius: RADIUS_COIN,
    textTransform: 'none' as const,
    fontFamily: 'Jost',
    fontWeight: 500,
    fontSize: '0.75rem',
    color: NAVY,
    borderColor: `${NAVY}1f`,
    px: 2,
    '&:hover': { backgroundColor: NAVY, color: '#fff', borderColor: NAVY },
  } as const;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: SURFACE }}>
      {!openSoldCarsAnalysis && (
        <>
          <Box sx={{ px: 2, pt: 1.5, flexShrink: 0 }}>
            <MasterFilter
              {...masterFilter}
              dynamicOptions={dynamicOptions}
              onAdvancedClick={() => setAdvancedOpen(true)}
              advancedFilterCount={filterModel.items.filter((i) => i.field !== 'isUsed').length}
              externalChips={advancedChips}
              onRemoveExternalChip={(key) =>
                setFilterModel({ items: filterModel.items.filter((item) => item.field !== key) })
              }
              onClearAllExternalChips={() =>
                setFilterModel({ items: filterModel.items.filter((item) => item.field === 'isUsed') })
              }
            />
            <SmartFilterModal
              open={isAdvancedOpen}
              onClose={() => setAdvancedOpen(false)}
              fieldDefs={carsFilterFields}
              initialItems={filterModel.items}
              onApply={(items) => setFilterModel({ items })}
              context={{ businessUnitType: null }}
            />
          </Box>

          {/* Quick actions + TODOS/USADOS/NUEVOS tabs */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              px: 2,
              pt: 1.5,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              {user?.isAdmin && (
                <>
                  <Button variant="outlined" size="small" onClick={() => navigate('/carModels')} sx={pillBtnSx}>
                    Ver Modelos
                  </Button>
                  <Button variant="outlined" size="small" onClick={() => setOpenSoldCarsAnalysis(true)} sx={pillBtnSx}>
                    Análisis de Ventas
                  </Button>
                </>
              )}
            </Box>
            <PillTabs
              tabs={isUsedTabs}
              value={isUsedToggleValue}
              onChange={handleIsUsedToggle}
              ariaLabel="Filtro por tipo de venta"
            />
          </Box>

          {/* Data Grid */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              m: 2,
              mt: 1.5,
              p: 2,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: 3,
              boxShadow: SHADOW,
            }}
          >
            {!carsData && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                <Skeleton variant="rectangular" height={40} />
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} variant="rectangular" height={48} />
                ))}
              </Box>
            )}
            {carsData && (
              <Fade in timeout={300}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                  <CustomDataGrid<Car>
                    rows={carsData.cars}
                    columns={columns}
                    filterModel={filterModel}
                    sortModel={sortModel}
                    onFilterModelChange={setFilterModel}
                    onSortModelChange={setSortModel}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    rowCount={carsData.total}
                    getRowClassName={(params) => (params.id === selectedCarId ? 'highlighted-1' : '')}
                    pageId="cars"
                    disableFilters
                  />
                </Box>
              </Fade>
            )}
          </Box>
        </>
      )}
      {selectedCarId && isRecommendClientsOpen && (
        <RecommendClientsModal open onClose={handleCloseRecommendModal} carId={selectedCarId} />
      )}
      {selectedCarId && isAnalyzePriceOpen && (
        <PriceAnalysisModal open onClose={handleClosePriceAnalysisModal} carId={selectedCarId} />
      )}
      {openSoldCarsAnalysis && (
        <SoldCarsAnalysis businessUnitId={businessUnitId} onClose={() => setOpenSoldCarsAnalysis(false)} />
      )}
    </Box>
  );
};

export default Cars;
